import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Exterior body, cedar lap siding, all four elevations, scrape loose paint, spot-prime bare wood, 2 coats satin', amount: '$5,200' },
  { item: 'Trim, fascia and soffits, 1 coat semi-gloss; replace 12 ft of rotted fascia board', amount: '$1,350' },
  { item: 'Front door and shutters, 2 coats, color change', amount: '$420' },
  { item: 'Optional: garage door and side man-door to match the trim', amount: '$380' },
];

const faq = [
  {
    q: 'Can I use this painting estimate template for interior and exterior jobs?',
    a: 'Yes. The surface grid works for rooms or for elevations, and the terms section has separate lines for interior access and exterior weather.',
  },
  {
    q: 'Why does the template separate prep from painting?',
    a: 'Because prep is where estimates differ most and where customers see the least. Showing scraping, patching and priming as their own lines explains why your number is what it is.',
  },
  {
    q: 'Where do colors go?',
    a: 'In the products section, or as "colors to be confirmed by [date]". The color-change line covers what happens if the customer switches after paint is bought.',
  },
  {
    q: 'Can a homeowner sign the estimate online?',
    a: 'Build it in Scopie and it is emailed to the homeowner as a link with your company name. They can tick optional surfaces such as the garage door and sign on their phone.',
  },
];

const page: TemplatePage = {
  slug: 'painting-estimate-template',
  path: '/templates/painting-estimate-template',
  primaryKeyword: 'painting estimate template',
  secondaryKeywords: ['painting quote template', 'exterior painting estimate template', 'interior painting estimate form'],
  title: 'Free Painting Estimate Template (PDF) | Scopie', // 46
  metaDescription:
    'A free painting estimate template with a surface grid for rooms or elevations, separate prep lines, coats and sheen, and color-change and weather terms.', // 153
  h1: 'Painting estimate template',
  answerFirst:
    'A painting estimate should list each room or elevation with its surfaces, show prep as separate work, and name the product, sheen and number of coats. This free template covers interior and exterior jobs, with terms for color changes, weather and rot found during prep, and a filled-in exterior example.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'document',
      id: 'template',
      h2: 'The estimate form',
      intro: 'Work through it room by room (interior) or side by side (exterior).',
      pdfName: 'painting-estimate-template',
      sections: [
        { heading: 'Painter', lines: ['[Company name] · [Phone] · [Email]'] },
        { heading: 'Homeowner', lines: ['[Name] · [Address]', 'Estimate [number] · [date] · valid until [date]'] },
        {
          heading: 'Surfaces',
          lines: [
            '[Room or elevation]: walls [yes / no] · ceiling [yes / no] · trim [yes / no] · doors [count] · price [amount]',
            '[Room or elevation]: walls [yes / no] · ceiling [yes / no] · trim [yes / no] · doors [count] · price [amount]',
            '[Cabinets / deck / fence / garage door]: price [amount]',
          ],
        },
        {
          heading: 'Preparation',
          lines: ['Scrape and sand loose paint [where] · patch and caulk [where] · spot-prime or full prime [which]', 'Repairs found during prep (rot, failing plaster): shown and priced before they are done'],
        },
        { heading: 'Products', lines: ['[Brand and line] · sheen [flat / eggshell / satin / semi-gloss] · coats [number]', 'Colors: [colors] or to be confirmed by [date]'] },
        {
          heading: 'Terms',
          lines: [
            'Interior: furniture moved by [us / you]; floors covered; work hours [hours].',
            'Exterior: rain or cold delays move the date, not the price.',
            'Color change after paint is purchased: [your rule].',
            'Deposit: [amount or none]. Cancellation: [your terms].',
          ],
        },
        { heading: 'Approved', lines: ['[Homeowner name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'An exterior repaint, filled in',
      label: 'Example: cedar-sided colonial',
      lines: filled,
      total: '$7,350',
      totalLabel: 'Total with the garage doors',
      note: 'Example amounts only.',
    },
    {
      type: 'text',
      id: 'exterior-interior',
      h2: 'Interior and exterior estimates need different terms',
      paragraphs: [
        'Exterior work depends on temperature and rain, so the estimate needs a weather line and a note about what happens to the schedule. Interior work happens around a family, so access hours, room order and who moves furniture matter more. The template has both; delete the one you do not need.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Mistakes painters make on estimates',
      style: 'mistakes',
      items: [
        { title: 'Coats not stated', text: 'One coat over a color change rarely covers. Write the number.' },
        { title: 'Rot discovered and fixed for free', text: 'Use the repairs line: show it, price it, then fix it.' },
        { title: 'Ceilings assumed', text: 'Say whether ceilings are in or out for each room.' },
        { title: 'No validity date', text: 'Paint prices change; an estimate without an end date can bind you to old costs.' },
      ],
    },
    {
      type: 'list',
      id: 'walkthrough',
      h2: 'Notes to take on the walk-through',
      style: 'checklist',
      items: [
        { text: 'Existing sheen and whether the old paint is oil or latex on trim.' },
        { text: 'Stains, water marks or smoke damage that need a stain-blocking primer.' },
        { text: 'Heights: stairwells, vaulted ceilings, second-storey gables and what equipment they need.' },
        { text: 'Lead-safe practices for homes built before 1978, where they apply.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Painting estimate template FAQ', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/painting', anchor: 'Scopie for painting contractors', status: 'live' },
    { href: '/templates/flooring-estimate-template', anchor: 'flooring estimate template', status: 'live' },
    { href: '/templates/cancellation-policy', anchor: 'cancellation policy template', status: 'live' },
  ],
  cta: {
    heading: 'Email your next painting estimate',
    text: 'Save your surfaces, prep items and products in Scopie, and send each estimate as a link the homeowner can sign.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
